import React from 'react';

import { AutocompleteFormField } from '@components/form-fields/AutocompleteFormField';
import { BooleanRecord, IAbstractRecord, IPagination } from '@models';

import EnhancedInternationalPhoneInput from '../_old/form/EnhancedInternationalPhoneInput';
import CheckboxFormField from '../form-fields/CheckboxFormField';
import CurrencyFormField from '../form-fields/CurrencyFormField';
import DatePickerFormField from '../form-fields/DatePickerFormField';
import DateRangeFormField from '../form-fields/DateRangeFormField';
import DateTimePickerFormField from '../form-fields/DateTimePickerFormField';
import MultiAutocompleteFormField from '../form-fields/MultiAutocompleteFormField';
import MultiselectFormField from '../form-fields/MultiselectFormField';
import NumberInputFormField from '../form-fields/NumberInputFormField';
import PasswordFormField from '../form-fields/PasswordFormField';
import PercentageFormField from '../form-fields/PercentageFormField';
import RepeaterBoxFormField from '../form-fields/repeater-box/RepeaterBoxFormField';
import SelectFormField from '../form-fields/SelectFormField';
import SplitPrefixFormField from '../form-fields/SplitPrefixFormField';
import SwitchFormField from '../form-fields/SwitchFormField';
import TextAreaFormField from '../form-fields/TextAreaFormField';
import TextInputFormField from '../form-fields/TextInputFormField';
import UploadFileFormField from '../form-fields/UploadFileFormField';
import { DynamicFormWidgetInputType, FormFieldType } from './types/inputTypes';
import {
  InitialMultiAutocompleteSearch,
  OnAutocompleteSearch,
  OnFieldBlur,
  OnFieldChange
} from './types';

type IDynamicFormInputProps = DynamicFormWidgetInputType & {
  onFieldChange: OnFieldChange;
  onFieldBlur: OnFieldBlur;
  loadingFields: Record<string, boolean | BooleanRecord>;
  sectionIndex: number;
  onAutocompleteSearch: OnAutocompleteSearch;
  initialMultiAutocompleteSearch: InitialMultiAutocompleteSearch;
  values: IAbstractRecord;
  errors: IAbstractRecord;
  touched: IAbstractRecord;
};

const DynamicFormInput: React.FC<IDynamicFormInputProps> = (inputProps) => {
  const {
    name,
    title,
    type,
    placeholder,
    required,
    disabled,
    material,
    hidden,
    conditionalDisable,
    conditionalHidden,
    conditionalRequired,
    onAutocompleteSearch,
    initialMultiAutocompleteSearch,
    onFieldChange,
    onFieldBlur,
    loadingFields,
    sectionIndex,
    values,
    errors,
    touched,
    ...rest
  } = inputProps;

  const customProps = rest as IAbstractRecord;
  const onFieldChangeByEvent = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onFieldChange(event.target.name, event.target.value, sectionIndex);
  };

  const renderInput = () => {
    const isDisabled =
      disabled || (conditionalDisable && conditionalDisable(values));
    const isHidden = hidden || (conditionalHidden && conditionalHidden(values));
    const isRequired =
      required || (conditionalRequired && conditionalRequired(values));
    const inputTitle = isRequired ? `${title}*` : title;

    if (isHidden) return null;

    switch (type) {
      case FormFieldType.text:
        return (
          <TextInputFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            disabled={isDisabled}
            onChange={onFieldChangeByEvent}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => onFieldBlur(name)}
          />
        );

      case FormFieldType.textArea:
        return (
          <TextAreaFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            rows={customProps.numRows}
            disabled={isDisabled}
            onChange={onFieldChangeByEvent}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => onFieldBlur(name)}
          />
        );

      case FormFieldType.datetime:
        return (
          <DateTimePickerFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            disabled={isDisabled}
            minDate={customProps.minDate}
            maxDate={customProps.maxDate}
            canClearDate={customProps.canClearDate}
            onDateChange={(v) => onFieldChange(name, v, sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
          />
        );

      case FormFieldType.splitPrefix:
        return (
          <SplitPrefixFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            inputType={customProps.inputType}
            prefixValue={customProps.prefixValue}
            disabled={isDisabled}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onChange={onFieldChangeByEvent}
            onBlur={() => onFieldBlur(name)}
          />
        );

      case FormFieldType.currency:
        return (
          <CurrencyFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            allowNegative={customProps.allowNegative}
            currencySymbol={customProps.currencySymbol}
            disabled={isDisabled}
            maxDecimalPrecision={customProps.maxDecimalPrecision}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onChange={onFieldChangeByEvent}
            onBlur={() => onFieldBlur(name)}
          />
        );

      case FormFieldType.number:
        return (
          <NumberInputFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            minValue={customProps.minValue}
            maxValue={customProps.maxValue}
            allowNegativeValues={customProps.allowNegativeValues}
            allowExponential={customProps.allowExponential}
            formatOnBlur={customProps.formatOnBlur}
            maxDecimalPrecision={customProps.maxDecimalPrecision}
            disabled={isDisabled}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onChange={onFieldChangeByEvent}
            onBlur={() => onFieldBlur(name)}
          />
        );

      case FormFieldType.percentage:
        return (
          <PercentageFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            disableNegativeValues={customProps.disableNegativeValues}
            minValue={customProps.minValue}
            maxValue={customProps.maxValue}
            disabled={isDisabled}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onChange={onFieldChangeByEvent}
            onBlur={() => onFieldBlur(name)}
          />
        );

      case FormFieldType.select:
        return customProps.injectGlobalLov ? (
          <SelectFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            injectGlobalLov={customProps.injectGlobalLov}
            canClearValue={customProps.canClearValue}
            freeSolo={customProps.freeSolo}
            loader={(loadingFields[name] as boolean) || false}
            disabled={isDisabled}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => onFieldBlur(name)}
          />
        ) : (
          <SelectFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            selectOptions={customProps.selectOptions}
            canClearValue={customProps.canClearValue}
            freeSolo={customProps.freeSolo}
            loader={(loadingFields[name] as boolean) || false}
            disabled={isDisabled}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => onFieldBlur(name)}
          />
        );

      case FormFieldType.multiSelect:
        return customProps.injectGlobalLov ? (
          <MultiselectFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            values={values[name]}
            injectGlobalLov={customProps.injectGlobalLov}
            freeSolo={customProps.freeSolo}
            maxTagCount={customProps.maxTagCount}
            loader={(loadingFields[name] as boolean) || false}
            disabled={isDisabled}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => onFieldBlur(name)}
            showSelectAll={customProps.showSelectAll}
            sortByLabel={customProps.sortByLabel}
          />
        ) : (
          <MultiselectFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            values={values[name]}
            selectOptions={customProps.selectOptions}
            freeSolo={customProps.freeSolo}
            maxTagCount={customProps.maxTagCount}
            loader={(loadingFields[name] as boolean) || false}
            disabled={isDisabled}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => onFieldBlur(name)}
            showSelectAll={customProps.showSelectAll}
            sortByLabel={customProps.sortByLabel}
          />
        );

      case FormFieldType.date:
        return (
          <DatePickerFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            disabled={isDisabled}
            minDate={customProps.minDate}
            maxDate={customProps.maxDate}
            canClearDate={customProps.canClearDate}
            onDateChange={(v) => onFieldChange(name, v, sectionIndex)}
            onInvalidDate={() => onFieldChange(name, values[name], sectionIndex)}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => onFieldBlur(name)}
          />
        );

      case FormFieldType.dateRange:
        return (
          <DateRangeFormField
            key={name}
            name={name}
            title={title}
            value={values[name]}
            error={touched[name] ? errors[name] : ''}
            onDateRangeChange={(dateValue) =>
              onFieldChange(name, dateValue, sectionIndex)
            }
            disabled={customProps.inputDisabled}
            material={material}
            minDate={customProps.minDate}
            maxDate={customProps.maxDate}
            placeholder={placeholder}
          />
        );
      case FormFieldType.autocomplete:
        return (
          <AutocompleteFormField
            key={name}
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            error={touched[name] ? errors[name] : ''}
            disabled={isDisabled}
            material={material}
            loader={(loadingFields[name] as boolean) || false}
            initialOptions={customProps?.initialOptions}
            disableClearable={!customProps.canClearValue}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            fetchOptions={async (query: string) => {
              if (onAutocompleteSearch) {
                const result = await onAutocompleteSearch(
                  name,
                  query,
                  { pageNumber: 1, pageSize: 10 },
                  false
                );

                return result.options;
              }

              return {};
            }}
          />
        );

      case FormFieldType.phonenbtext:
        return (
          <TextInputFormField
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            disabled={isDisabled}
            onChange={onFieldChangeByEvent}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => {
              onFieldBlur(name);
            }} // Limit to 8 characters
          />
        );
      case FormFieldType.multiAutocomplete:
        return (
          <MultiAutocompleteFormField
            key={name}
            name={name}
            title={title}
            placeholder={placeholder}
            value={values[name]}
            disabled={isDisabled}
            material={material}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            onBlur={() => onFieldBlur(name)}
            maxItemsDisplayed={customProps.maxItemsDisplayed}
            canClearValue={customProps.canClearValue}
            fetchOptions={(query: string, pagination: IPagination) => {
              if (onAutocompleteSearch) {
                return onAutocompleteSearch(name, query, pagination);
              }

              return Promise.resolve({ options: {}, totalCount: 0 });
            }}
            fetchOptionsByValues={(
              values: string[],
              pagination: IPagination
            ) => {
              if (initialMultiAutocompleteSearch) {
                return initialMultiAutocompleteSearch(name, values, pagination);
              }

              return Promise.resolve({ options: {}, totalCount: 0 });
            }}
          />
        );

      case FormFieldType.repeaterBox:
        return (
          <RepeaterBoxFormField
            key={name}
            title={title}
            values={values[name]}
            error={errors[name]}
            inputs={customProps.inputs}
            filterDuplicatesByKey={customProps.filterDuplicatesByKey || ''}
            addButtonText={customProps.addButtonText}
            dynamicAddButtonText={customProps.dynamicAddButtonText}
            loadingFields={loadingFields[name] as BooleanRecord}
            maxRepeaterLength={customProps.maxRepeaterLength}
            minRepeaterLength={customProps.minRepeaterLength}
            onChange={(v, subFieldName) => {
              onFieldChange(name, v, sectionIndex, {
                index: sectionIndex,
                subFieldName,
                input: inputProps
              });
            }}
            onBlur={(subFieldName: string, index: number) => {
              onFieldBlur(name, { subFieldName, index, input: inputProps });
            }}
            onAutocompleteSearch={onAutocompleteSearch}
            disabled={isDisabled}
            material={material}
            touched={touched[name]}
          />
        );

      case FormFieldType.fileUploader:
        return (
          <UploadFileFormField
            key={name}
            name={name}
            title={inputTitle}
            value={values[name]}
            error={touched[name] ? errors[name] : ''}
            onUpload={(file) => {
              onFieldChange(name, file, sectionIndex);
              if (customProps.onUpload) customProps.onUpload(file);
            }}
            disabled={isDisabled}
            placeholder={placeholder}
            allowedFileTypes={customProps.allowedFileTypes}
            maxFileSizeInMB={customProps.maxFileSizeInMB}
            downloadLinks={customProps.downloadLinks || []}
            downloadButton={customProps.downloadButton || []}
          />
        );

      case FormFieldType.switch:
        return (
          <SwitchFormField
            key={name}
            name={name}
            title={inputTitle}
            value={values[name]}
            onChange={(n, v) => onFieldChange(n, v, sectionIndex)}
            onBlur={() => onFieldBlur(name)}
            disabled={isDisabled}
            required={required}
            information={customProps.tooltipText}
          />
        );

      case FormFieldType.phoneNumber:
        return (
          <EnhancedInternationalPhoneInput
            key={name}
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            error={touched[name] ? errors[name] : ''}
            onChange={(v) => onFieldChange(name, v, sectionIndex)}
            onBlur={() => onFieldBlur(name)}
            disabled={isDisabled}
            countriesToShow={customProps.countriesToShow}
            disableDropDown={customProps.disableDropDown}
            editCountryCode={customProps.editCountryCode}
            defaultCountry={customProps.defaultCountry}
            customFormat={customProps.customFormat}
            material={material}
          />
        );

      case FormFieldType.password:
        return (
          <PasswordFormField
            key={name}
            name={name}
            title={inputTitle}
            placeholder={placeholder}
            value={values[name]}
            disabled={isDisabled}
            hideHint={customProps.hideHint}
            strongValidation={customProps.strongValidation}
            onChange={onFieldChangeByEvent}
            error={touched[name] ? errors[name] : ''}
            material={material}
            onBlur={() => onFieldBlur(name)}
          />
        );

      case FormFieldType.checkbox:
        return (
          <CheckboxFormField
            key={name}
            name={name}
            title={inputTitle}
            checked={values[name]}
            onChange={(n, v) => onFieldChange(n, v, sectionIndex)}
            disabled={isDisabled}
          />
        );
      default:
        return <></>;
    }
  };

  return renderInput();
};

export default DynamicFormInput;
